import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, ArrowLeft, ChevronRight, Clock, FileText, Flag, MapPin, Navigation, PauseCircle, UserRound, X } from 'lucide-react'
import type { TimsTrip } from '../lib/types'
import { LATE_KEYING_MIN, type TripLr } from './tripLogistics'
import type { Alert, Severity } from '../lib/types'
import { clock, dateTime } from '../lib/format'
import { fmtDuration } from './RoundTripCycles'
import type { TripReplayPayload } from './TripReplay'

/**
 * Right-hand panel for a trip replay: everything that happened on the trip, in order --
 * the Dhar/Jhulwaniya/Dhule milestones, every alert, every halt and every named place the
 * truck passed -- as one vertical timeline. Clicking a row seeks the replay there; the row
 * at the playhead is highlighted and the list follows it while the replay plays.
 */

export type TimelineKind = 'milestone' | 'alert' | 'halt' | 'place' | 'lr' | 'now'

export interface TimelineItem {
  id: string
  ts: number
  kind: TimelineKind
  title: string
  sub?: string
  severity?: Severity
  /** Seconds -- a halt's or a place visit's length. */
  durationSec?: number
  /** A TIMS gate scan: which LR it belongs to, and whether it was keyed in late. */
  lr?: string
  late?: boolean
}

const MILESTONES: { key: keyof NonNullable<TripReplayPayload['playback']>['milestones']; title: string }[] = [
  { key: 'left_dhar', title: 'Left Dhar' },
  { key: 'jhulwaniya_out', title: 'Passed Jhulwaniya' },
  { key: 'reached_dhule', title: 'Reached Dhule' },
  { key: 'left_dhule', title: 'Left Dhule' },
  { key: 'jhulwaniya_back', title: 'Passed Jhulwaniya · return' },
  { key: 'back_at_dhar', title: 'Back at Dhar' },
]

function prettyKind(kind: string): string {
  const s = kind.replace(/_/g, ' ').toLowerCase()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Pure: the trip's events as one ordered list. Hub boundaries (DHAR/JHUL/DHULE) come from
 *  the GPS milestones, so geotags for those families are not repeated as places; an
 *  ENTER + EXIT of the same fence collapses into one "Passed <place>" row with its dwell. */
export function buildTripTimeline(payload: TripReplayPayload, alerts: readonly Alert[], now = Date.now()): TimelineItem[] {
  const items: TimelineItem[] = []
  const ms = payload.playback?.milestones
  if (ms) {
    for (const m of MILESTONES) {
      const ts = ms[m.key]
      if (ts != null) items.push({ id: `m:${m.key}`, ts, kind: 'milestone', title: m.title })
    }
  }
  const isHub = (code?: string | null) => !!code && /^(DHAR|DHULE|JHUL)/i.test(code)
  const open = new Map<string, { ts: number; name: string }>()
  for (const e of payload.events) {
    if (e.kind === 'halt') {
      const sec = e.duration_sec ?? (e.end_ts && e.ts ? Math.round((e.end_ts - e.ts) / 1000) : undefined)
      items.push({
        id: `h:${e.ts}`, ts: e.ts, kind: 'halt',
        title: sec != null ? `Halted ${fmtDuration(sec)}` : 'Halted',
        sub: e.geofence_name || e.hub_code || undefined,
        durationSec: sec,
      })
      continue
    }
    const name = e.geofence_name || e.hub_code
    if (!name || isHub(e.hub_code) || !e.geofence_id) continue
    if (e.event === 'ENTER') {
      open.set(e.geofence_id, { ts: e.ts, name })
    } else if (e.event === 'EXIT') {
      const enter = open.get(e.geofence_id)
      open.delete(e.geofence_id)
      const ts = enter?.ts ?? e.ts
      const sec = enter ? Math.round((e.ts - enter.ts) / 1000) : e.dwell_sec ?? undefined
      items.push({
        id: `p:${e.geofence_id}:${ts}`, ts, kind: 'place', title: `Passed ${name}`,
        sub: sec != null && sec >= 60 ? `${fmtDuration(sec)} inside` : undefined, durationSec: sec,
      })
    }
  }
  for (const [fid, v] of open) items.push({ id: `p:${fid}:${v.ts}`, ts: v.ts, kind: 'place', title: `Entered ${v.name}` })
  for (const a of alerts) {
    items.push({
      id: `a:${a.alert_id}`, ts: a.ts, kind: 'alert', severity: a.severity,
      title: prettyKind(a.kind) + (a._groupCount && a._groupCount > 1 ? ` ×${a._groupCount}` : ''),
      sub: a.message || undefined,
    })
  }
  // TIMS gate scans of the trip's LR(s) -- the register's view of the same journey.
  const winFrom = payload.started_ts - 2 * 3_600_000
  const winTo = payload.ended_ts ?? payload.live_until ?? now
  for (const lr of payload.logistics?.lrs ?? []) {
    for (const st of lr.stages) {
      if (st.event_ts == null || st.event_ts < winFrom || st.event_ts > winTo) continue
      const late = st.lag_min != null && st.lag_min > LATE_KEYING_MIN
      items.push({
        id: `l:${lr.lr_number}:${st.event_code}:${st.event_ts}`, ts: st.event_ts, kind: 'lr', lr: lr.lr_number, late,
        title: st.label,
        sub: `LR ${lr.lr_number}${st.lag_min != null ? ` · keyed ${st.lag_min} min later` : ''}`,
      })
    }
  }
  if (payload.playback?.status === 'live' && payload.live_until) {
    items.push({ id: 'now', ts: payload.live_until ?? now, kind: 'now', title: 'Now · still on the road' })
  }
  return items.sort((x, y) => x.ts - y.ts || order(x.kind) - order(y.kind))
}

function order(k: TimelineKind): number {
  return { milestone: 0, lr: 1, place: 2, halt: 3, alert: 4, now: 5 }[k]
}

type Filter = 'all' | 'alert' | 'halt' | 'place' | 'lr'
const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'alert', label: 'Alerts' },
  { id: 'halt', label: 'Stops' },
  { id: 'place', label: 'Places' },
  { id: 'lr', label: 'Gate scans' },
]

const SEV_COLOR: Record<string, string> = { CRITICAL: '#DC2626', WARN: '#D97706', INFO: '#2563EB' }

function dotStyle(it: TimelineItem): { bg: string; ring: string } {
  switch (it.kind) {
    case 'milestone': return { bg: '#2563EB', ring: 'rgba(37,99,235,0.18)' }
    case 'alert': return { bg: SEV_COLOR[it.severity ?? 'INFO'] ?? '#2563EB', ring: 'rgba(220,38,38,0.14)' }
    case 'halt': return { bg: '#D97706', ring: 'rgba(217,119,6,0.16)' }
    case 'now': return { bg: '#10B981', ring: 'rgba(16,185,129,0.2)' }
    case 'lr': return { bg: it.late ? '#D97706' : '#4F46E5', ring: 'rgba(79,70,229,0.16)' }
    default: return { bg: '#64748B', ring: 'rgba(100,116,139,0.14)' }
  }
}

function Icon({ it }: { it: TimelineItem }): JSX.Element {
  const p = { size: 12, className: 'text-white' }
  if (it.kind === 'milestone') return <Flag {...p} />
  if (it.kind === 'alert') return <AlertTriangle {...p} />
  if (it.kind === 'halt') return <PauseCircle {...p} />
  if (it.kind === 'now') return <Navigation {...p} />
  if (it.kind === 'lr') return <FileText {...p} />
  return <MapPin {...p} />
}

export function TripTimelinePanel({
  payload, alerts, at, playing, onSeek, onClose, variant = 'card', openLr = null, onOpenLr, onCloseLr, liveTims,
}: {
  /** LR whose details replace the timeline (clicked on the play bar or a gate-scan row). */
  openLr?: string | null
  onOpenLr?: (lr: string) => void
  onCloseLr?: () => void
  /** The truck's CURRENT TIMS trip (live trips only) -- vendor, trailer, status, delay. */
  liveTims?: TimsTrip

  /** 'card': a floating rounded panel. 'column': fills its column edge to edge (the
   *  Command Centre's full-height right column beside the map). */
  variant?: 'card' | 'column'
  payload: TripReplayPayload
  alerts: readonly Alert[]
  at: number
  playing: boolean
  onSeek: (ts: number) => void
  onClose: () => void
}): JSX.Element {
  const items = useMemo(() => buildTripTimeline(payload, alerts), [payload, alerts])
  const [filter, setFilter] = useState<Filter>('all')
  const shown = useMemo(() => (filter === 'all' ? items : items.filter((i) => i.kind === filter || i.kind === 'milestone')), [items, filter])
  const counts = useMemo(() => ({
    all: items.length,
    alert: items.filter((i) => i.kind === 'alert').length,
    halt: items.filter((i) => i.kind === 'halt').length,
    place: items.filter((i) => i.kind === 'place').length,
    lr: items.filter((i) => i.kind === 'lr').length,
  }), [items])
  const critical = items.filter((i) => i.kind === 'alert' && i.severity === 'CRITICAL').length
  const haltSec = items.reduce((s, i) => s + (i.kind === 'halt' ? i.durationSec ?? 0 : 0), 0)
  const durSec = payload.playback?.duration_sec

  // The row at the playhead: the last one at or before it.
  const currentId = useMemo(() => {
    let id: string | null = null
    for (const i of shown) if (i.ts <= at) id = i.id
    return id
  }, [shown, at])

  // Follow the playhead while playing (not while the reader is browsing a paused replay).
  const listRef = useRef<HTMLOListElement | null>(null)
  useEffect(() => {
    if (!playing || !currentId || !listRef.current) return
    const el = listRef.current.querySelector<HTMLElement>(`[data-id="${CSS.escape(currentId)}"]`)
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [currentId, playing])

  const lrData = openLr ? payload.logistics?.lrs.find((l) => l.lr_number === openLr) ?? null : null
  const shell = variant === 'column'
    ? 'flex h-full w-full flex-col overflow-hidden bg-white'
    : 'flex h-full w-[340px] flex-col overflow-hidden rounded-2xl border border-[rgba(15,23,42,0.08)] bg-white/95 shadow-[0_12px_32px_rgba(15,23,42,0.14)] backdrop-blur'
  if (openLr) {
    return (
      <aside aria-label={`LR ${openLr}`} className={shell}>
        <LrDetails
          lrNumber={openLr} lr={lrData} vehicleId={payload.vehicle_id}
          liveTims={liveTims && liveTims.lr_number?.trim() === openLr ? liveTims : undefined}
          at={at} onSeek={onSeek} onBack={() => onCloseLr?.()} onClose={onClose}
        />
      </aside>
    )
  }

  return (
    <aside
      aria-label="Trip timeline"
      className={variant === 'column'
        ? 'flex h-full w-full flex-col overflow-hidden bg-white'
        : 'flex h-full w-[340px] flex-col overflow-hidden rounded-2xl border border-[rgba(15,23,42,0.08)] bg-white/95 shadow-[0_12px_32px_rgba(15,23,42,0.14)] backdrop-blur'}
    >
      <header className="border-b border-[rgba(15,23,42,0.06)] px-4 pb-3 pt-3">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-[#2563EB]" />
          <h2 className="text-[13px] font-bold text-[#0F172A]">Trip timeline</h2>
          <span className="font-mono text-[11.5px] font-semibold text-[#475569]">{payload.vehicle_id}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Hide trip timeline"
            className="ml-auto flex h-7 w-7 items-center justify-center rounded-lg text-[#64748B] transition-colors hover:bg-[#F1F5F9] hover:text-[#0F172A]"
          >
            <X size={14} />
          </button>
        </div>
        <dl className="mt-3 grid grid-cols-3 gap-2">
          <div className="rounded-lg bg-[#F8FAFC] px-2 py-1.5">
            <dt className="text-[9.5px] font-semibold uppercase tracking-wide text-[#94A3B8]">{payload.playback?.status === 'live' ? 'So far' : 'Duration'}</dt>
            <dd className="text-[12.5px] font-bold text-[#0F172A]">{durSec != null ? fmtDuration(durSec) : '—'}</dd>
          </div>
          <div className="rounded-lg bg-[#F8FAFC] px-2 py-1.5">
            <dt className="text-[9.5px] font-semibold uppercase tracking-wide text-[#94A3B8]">Alerts</dt>
            <dd className="text-[12.5px] font-bold text-[#0F172A]">
              {counts.alert}
              {critical > 0 && <span className="ml-1 text-[10.5px] font-semibold text-[#DC2626]">{critical} critical</span>}
            </dd>
          </div>
          <div className="rounded-lg bg-[#F8FAFC] px-2 py-1.5">
            <dt className="text-[9.5px] font-semibold uppercase tracking-wide text-[#94A3B8]">Stopped</dt>
            <dd className="text-[12.5px] font-bold text-[#0F172A]">{haltSec > 0 ? fmtDuration(haltSec) : '—'}</dd>
          </div>
        </dl>
        <div className="mt-3 flex gap-1" role="group" aria-label="Show">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              aria-pressed={filter === f.id}
              onClick={() => setFilter(f.id)}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                filter === f.id ? 'bg-[#0F172A] text-white' : 'bg-[#F1F5F9] text-[#475569] hover:text-[#0F172A]'
              }`}
            >
              {f.label}
              <span className={`tabular-nums ${filter === f.id ? 'text-white/70' : 'text-[#94A3B8]'}`}>{counts[f.id]}</span>
            </button>
          ))}
        </div>
      </header>

      {shown.length === 0 ? (
        <p className="px-4 py-8 text-center text-[12px] text-[#64748B]">Nothing of this kind on this trip.</p>
      ) : (
        <ol ref={listRef} className="relative min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {shown.map((it, idx) => {
            const past = it.ts <= at
            const isCurrent = it.id === currentId
            const dot = dotStyle(it)
            const last = idx === shown.length - 1
            const newDay = idx === 0 || new Date(shown[idx - 1]!.ts).toDateString() !== new Date(it.ts).toDateString()
            return (
              <li key={it.id} data-id={it.id}>
                {newDay && (
                  <div className="mb-1.5 mt-0.5 pl-[52px] text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">
                    {new Date(it.ts).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onSeek(it.ts)}
                  onDoubleClick={it.lr ? () => onOpenLr?.(it.lr!) : undefined}
                  title={`${dateTime(it.ts)} · jump the replay here${it.lr ? ' · double-click for the LR' : ''}`}
                  className={`group relative flex w-full items-start gap-2.5 rounded-xl px-1.5 py-1.5 text-left transition-colors ${
                    isCurrent ? 'bg-[#EFF6FF] ring-1 ring-[rgba(37,99,235,0.25)]' : 'hover:bg-[#F8FAFC]'
                  } ${past || isCurrent ? '' : 'opacity-55'}`}
                >
                  <span className={`w-[38px] shrink-0 pt-1 text-right font-mono text-[10.5px] tabular-nums ${isCurrent ? 'font-bold text-[#1D4ED8]' : 'text-[#64748B]'}`}>
                    {clock(it.ts).slice(0, 5)}
                  </span>
                  <span className="relative flex shrink-0 flex-col items-center self-stretch">
                    <span
                      className={`relative z-[1] flex items-center justify-center rounded-full ${it.kind === 'milestone' || it.kind === 'now' ? 'h-6 w-6' : 'h-5 w-5'}`}
                      style={{ background: dot.bg, boxShadow: `0 0 0 4px ${dot.ring}` }}
                    >
                      <Icon it={it} />
                    </span>
                    {!last && <span className="absolute top-6 h-[calc(100%+6px)] w-[2px] rounded" style={{ background: past ? '#BFDBFE' : '#E2E8F0' }} />}
                  </span>
                  <span className="min-w-0 flex-1 pb-1 pt-0.5">
                    <span className={`flex items-center gap-1.5 text-[12px] ${it.kind === 'milestone' ? 'font-bold text-[#0F172A]' : 'font-semibold text-[#1E293B]'}`}>
                      <span className="truncate">{it.title}</span>
                      {it.kind === 'alert' && it.severity && (
                        <span
                          className="shrink-0 rounded-full px-1.5 text-[9px] font-bold uppercase tracking-wide"
                          style={{ color: SEV_COLOR[it.severity] ?? '#2563EB', background: `${SEV_COLOR[it.severity] ?? '#2563EB'}14` }}
                        >
                          {it.severity}
                        </span>
                      )}
                      {it.kind === 'now' && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#10B981]" />}
                    </span>
                    {it.sub && (
                      <span className={`mt-0.5 block line-clamp-2 text-[11px] leading-snug ${it.late ? 'text-[#B45309]' : 'text-[#64748B]'}`}>{it.sub}</span>
                    )}
                  </span>
                  <ChevronRight size={13} className="mt-1 shrink-0 text-[#CBD5E1] opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              </li>
            )
          })}
        </ol>
      )}
    </aside>
  )
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'warn' }): JSX.Element {
  return (
    <div className="rounded-lg bg-[#F8FAFC] px-2.5 py-1.5">
      <dt className="text-[9.5px] font-semibold uppercase tracking-wide text-[#94A3B8]">{label}</dt>
      <dd className={`text-[12.5px] font-bold ${tone === 'warn' ? 'text-[#B45309]' : 'text-[#0F172A]'}`}>{value}</dd>
    </div>
  )
}

/** One LR, from the TIMS register: what was carried, who drove, and every gate scan with
 *  how late it was keyed in. Live TIMS fields (vendor, trailer, status, delay) when this is
 *  the truck's current trip. Clicking a scan jumps the replay to it. */
function LrDetails({
  lrNumber, lr: lrFromTrip, vehicleId, liveTims, at, onSeek, onBack, onClose,
}: {
  lrNumber: string
  lr: TripLr | null
  vehicleId: string
  liveTims?: TimsTrip
  at: number
  onSeek: (ts: number) => void
  onBack: () => void
  onClose: () => void
}): JSX.Element {
  // The full TIMS trip behind this LR -- everything the dispatch / gate staff keyed in by
  // hand -- from serving's latest TIMS poll (active AND completed trips). Fetched once per
  // LR opened; the live feed's current trip is the fallback while it loads or if absent.
  // The LR's own stored gate scans: from the trip when it was matched there, else fetched
  // by LR -- the panel must never show "0 gate scans" for an LR that has them.
  const [scans, setScans] = useState<TripLr | null>(null)
  useEffect(() => {
    if (lrFromTrip) return
    const ctl = new AbortController()
    fetch(`/api/round-trips/playback/lr/${encodeURIComponent(lrNumber)}`, { signal: ctl.signal })
      .then(async (r) => (r.ok ? setScans(await r.json() as TripLr) : setScans(null)))
      .catch(() => undefined)
    return () => ctl.abort()
  }, [lrNumber, lrFromTrip])
  const lr = lrFromTrip ?? scans
  const [tims, setTims] = useState<{ state: 'loading' | 'ok' | 'missing' | 'error'; trip?: TimsTrip; polledAt?: number | null; error?: string }>({ state: 'loading' })
  useEffect(() => {
    const ctl = new AbortController()
    setTims({ state: 'loading' })
    fetch(`/api/tims/lr/${encodeURIComponent(lrNumber)}`, { signal: ctl.signal })
      .then(async (r) => {
        const j = await r.json().catch(() => ({})) as { trip?: TimsTrip; polled_at?: number | null; error?: string }
        if (r.status === 404) return setTims({ state: 'missing' })
        if (!r.ok || !j.trip) throw new Error(j.error ?? `HTTP ${r.status}`)
        setTims({ state: 'ok', trip: j.trip, polledAt: j.polled_at ?? null })
      })
      .catch((e: unknown) => { if (!ctl.signal.aborted) setTims({ state: 'error', error: e instanceof Error ? e.message : String(e) }) })
    return () => ctl.abort()
  }, [lrNumber])
  const trip = tims.trip ?? liveTims
  const det = (trip?.details ?? {}) as Record<string, string | number | boolean | null>
  const dv = (k: string): string | null => (det[k] != null && det[k] !== '' ? String(det[k]) : null)
  const driver = lr?.driver_name ?? trip?.driver_name ?? null
  // TIMS's own driver code (tp_driver_id), never its internal UUID (driver_id)
  const driverCode = lr?.tp_driver_id ?? dv('tp_driver_id')
  const tonnage = lr?.tonnage ?? (trip?.tonnage != null && trip.tonnage !== '' ? Number(trip.tonnage) : null)
  const di = lr?.di_numbers.length ? lr.di_numbers.join(', ') : trip?.di_number ?? null

  const tripRows = trip ? compact([
    ['Status', trip.trip_closed ? `${trip.status ?? 'Closed'} · closed` : trip.status],
    ['Current stage', trip.trip_stage_event ?? trip.event],
    ['Trip date', trip.trip_date],
    ['Shift', trip.shift],
    ['Site', trip.site_name],
    ['Completed at', fmtMaybeTime(trip.trip_completed_time ?? dv('trip_completed_time'))],
    ['Exchange trip', trip.is_exchange == null ? null : trip.is_exchange ? 'Yes' : 'No'],
    ['Delay', (trip.delay_formatted ?? dv('delay_formatted'))
      ? `${trip.delay_formatted ?? dv('delay_formatted')}${(trip.delay_reason ?? dv('delay_reason')) ? ` · ${trip.delay_reason ?? dv('delay_reason')}` : ''}`
      : null],
    ['Delay (min)', trip.delay_formatted ?? dv('delay_formatted') ? null : trip.delay_min != null && trip.delay_min !== 0 ? `${trip.delay_min} min` : null],
    ['Flag', trip.badge_label ?? trip.flag_status ? `${trip.badge_label ?? trip.flag_status}${trip.flag_count ? ` (${trip.flag_count})` : ''}` : null],
  ]) : []
  const loadRows = trip ? compact([
    ['Vehicle (TIMS)', trip.tims_vehicle],
    ['Vehicle type', trip.vehicle_type],
    ['Trailer', trip.trailer ?? (dv('trailer_no') ? `${dv('trailer_no')}${dv('trailer_type') ? ` · ${dv('trailer_type')}` : ''}` : null)],
    ['Vendor / transporter', trip.vendor],
    ['Tonnage', tonnage != null ? `${tonnage} t` : null],
    ['In time', fmtMaybeTime(trip.in_time)],
    ['In odometer', trip.in_odo != null && trip.in_odo !== '' ? `${trip.in_odo} km` : null],
    ['In SoC', trip.in_soc != null && trip.in_soc !== '' ? `${trip.in_soc}%` : null],
    ['Completed odometer', dv('trip_completed_odo') ? `${dv('trip_completed_odo')} km` : null],
    ['Completed SoC', dv('trip_completed_soc') ? `${dv('trip_completed_soc')}%` : null],
  ]) : []
  const shown = new Set(['status', 'trip_stage_event', 'event', 'trip_date', 'shift', 'site_name', 'trip_completed_time', 'is_exchange',
    'delay_formatted', 'delay_reason', 'badge_label', 'flag_status', 'flag_count', 'vehicle_type', 'trailer', 'vendor', 'tonnage',
    'in_time', 'in_odo', 'in_soc', 'lr_number', 'di_number', 'driver_name', 'driver_id', 'delay_min',
    // the same values again under TIMS's raw names -- already shown above
    'vendor_name', 'shift_name', 'vehicle_no', 'transaction_odo', 'transaction_soc', 'transaction_time',
    'tp_driver_id', 'is_trip_completed', 'trailer_no', 'trailer_type', 'trip_completed_time', 'trip_completed_odo',
    'trip_completed_soc', 'delay_formatted', 'delay_reason'])
  const otherRows = Object.entries(trip?.details ?? {})
    .filter(([k, v]) => v != null && v !== '' && !shown.has(k) && !/(^|_)(id|uuid)$/.test(k))
    .map(([k, v]) => [prettyKey(k), fmtMaybeTime(v) ?? String(v)] as [string, string])
    .sort((a, b) => a[0].localeCompare(b[0]))

  return (
    <>
      <header className="border-b border-[rgba(15,23,42,0.06)] px-4 pb-3 pt-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to trip timeline"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-[#475569] transition-colors hover:bg-[#F1F5F9] hover:text-[#0F172A]"
          >
            <ArrowLeft size={15} />
          </button>
          <h2 className="inline-flex items-center gap-1.5 rounded-lg bg-[#4F46E5] px-2.5 py-1 text-[13px] font-bold text-white">
            <FileText size={13} aria-hidden /> LR {lrNumber}
          </h2>
          <span className="font-mono text-[11.5px] font-semibold text-[#475569]">{vehicleId}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Hide panel"
            className="ml-auto flex h-7 w-7 items-center justify-center rounded-lg text-[#64748B] transition-colors hover:bg-[#F1F5F9] hover:text-[#0F172A]"
          >
            <X size={14} />
          </button>
        </div>
        {di ? <p className="mt-1.5 pl-9 text-[11px] text-[#64748B]">DI <span className="font-semibold text-[#334155]">{di}</span></p> : null}

        {driver && (
          <div className="mt-3 flex items-center gap-2.5 rounded-xl bg-[#0F172A] px-3 py-2.5 text-white">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15">
              <UserRound size={15} />
            </span>
            <div className="min-w-0">
              <p className="truncate text-[13px] font-bold">{driver.replace(/\s+/g, ' ')}</p>
              <p className="text-[10.5px] text-white/60">Driver{driverCode ? ` · TIMS ${driverCode}` : ''}</p>
            </div>
          </div>
        )}

        <dl className="mt-3 grid grid-cols-2 gap-2">
          <Stat label="Tonnage" value={tonnage != null ? `${tonnage} t` : '—'} />
          <Stat label="Distance (odo)" value={lr?.distance_km != null ? `${lr.distance_km} km` : '—'} />
          <Stat label="Gate scans" value={String(lr?.stages.length ?? 0)} />
          <Stat
            label="Slowest keying"
            value={lr?.max_lag_min != null ? `${lr.max_lag_min} min` : '—'}
            tone={lr?.max_lag_min != null && lr.max_lag_min > LATE_KEYING_MIN ? 'warn' : undefined}
          />
        </dl>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {tims.state === 'loading' && !trip ? (
          <p className="mb-4 rounded-lg bg-[#F8FAFC] px-3 py-3 text-[11.5px] text-[#64748B]">Loading the TIMS trip…</p>
        ) : null}
        {(tims.state === 'missing' || tims.state === 'error') && !trip ? (
          <p className="mb-4 rounded-lg border border-dashed border-[rgba(15,23,42,0.12)] px-3 py-3 text-[11.5px] text-[#64748B]">
            {tims.state === 'missing'
              ? 'This LR is not in the latest TIMS poll -- the gate scans below are what was stored.'
              : `TIMS trip unavailable: ${tims.error}`}
          </p>
        ) : null}

        <KvSection title="Trip (TIMS)" rows={tripRows} live={!!liveTims && !tims.trip} />
        <KvSection title="Vehicle & load" rows={loadRows} />
        {otherRows.length > 0 && <KvSection title="All other TIMS fields" rows={otherRows} collapsible />}

        <h3 className="mb-1.5 text-[10.5px] font-bold uppercase tracking-wider text-[#94A3B8]">Gate scans</h3>
        {!lr || lr.stages.length === 0 ? (
          <p className="rounded-lg border border-dashed border-[rgba(15,23,42,0.12)] px-3 py-4 text-center text-[11.5px] text-[#64748B]">
            No gate scans stored for this LR yet.
          </p>
        ) : (
          <ol className="space-y-1.5">
            {lr.stages.map((st, i) => {
              const late = st.lag_min != null && st.lag_min > LATE_KEYING_MIN
              const past = st.event_ts != null && st.event_ts <= at
              return (
                <li key={`${st.event_code}-${st.event_ts}-${i}`}>
                  <button
                    type="button"
                    onClick={() => st.event_ts != null && onSeek(st.event_ts)}
                    className={`w-full rounded-xl border px-3 py-2 text-left transition-colors hover:border-[rgba(79,70,229,0.35)] ${
                      past ? 'border-[rgba(15,23,42,0.08)] bg-white' : 'border-dashed border-[rgba(15,23,42,0.1)] bg-[#FCFCFD]'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: late ? '#D97706' : '#4F46E5' }} />
                      <span className="text-[12px] font-semibold text-[#0F172A]">{st.label}</span>
                      <span className="ml-auto font-mono text-[11px] tabular-nums text-[#475569]">{st.event_ts ? dateTime(st.event_ts) : '—'}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 pl-4 text-[10.5px] text-[#64748B]">
                      {st.lag_min != null && (
                        <span className={late ? 'font-semibold text-[#B45309]' : undefined}>keyed {st.lag_min} min later</span>
                      )}
                      {st.odo_km != null && <span>odo {Math.round(st.odo_km).toLocaleString('en-IN')} km</span>}
                      {st.soc_pct != null && <span>SoC {st.soc_pct}%</span>}
                      {st.tonnage != null && <span>{st.tonnage} t</span>}
                      {st.site_name && <span>{st.site_name}</span>}
                    </div>
                  </button>
                </li>
              )
            })}
          </ol>
        )}
      </div>
    </>
  )
}

type Row = [string, string]

function compact(rows: [string, unknown][]): Row[] {
  return rows.filter(([, v]) => v != null && v !== '').map(([k, v]) => [k, String(v)])
}

function prettyKey(k: string): string {
  const s = k.replace(/^transaction_/, '').replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** ISO-ish timestamps from TIMS read as a local date-time; anything else passes through. */
function fmtMaybeTime(v: unknown): string | null {
  if (v == null || v === '') return null
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
    // TIMS's naive times are IST wall clock (serving/tims_trips.py); an offset is honoured.
    const ms = Date.parse(/[zZ]|[+-]\d{2}:?\d{2}$/.test(v) ? v : `${v}+05:30`)
    return Number.isFinite(ms) ? dateTime(ms) : v
  }
  return typeof v === 'string' ? v : String(v)
}

function KvSection({ title, rows, live, collapsible }: { title: string; rows: Row[]; live?: boolean; collapsible?: boolean }): JSX.Element | null {
  const [open, setOpen] = useState(!collapsible)
  if (!rows.length) return null
  return (
    <section className="mb-4">
      <button
        type="button"
        onClick={() => collapsible && setOpen((o) => !o)}
        className={`mb-1.5 flex w-full items-center gap-1.5 text-left text-[10.5px] font-bold uppercase tracking-wider text-[#94A3B8] ${collapsible ? 'cursor-pointer hover:text-[#475569]' : 'cursor-default'}`}
        aria-expanded={collapsible ? open : undefined}
      >
        {live && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#10B981]" />}
        {title}
        {collapsible && <span className="ml-auto normal-case tracking-normal">{open ? 'Hide' : `Show ${rows.length}`}</span>}
      </button>
      {open && (
        <dl className="divide-y divide-[rgba(15,23,42,0.05)] overflow-hidden rounded-xl border border-[rgba(15,23,42,0.07)]">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-baseline justify-between gap-3 px-3 py-1.5 even:bg-[#FCFCFD]">
              <dt className="shrink-0 text-[11px] text-[#64748B]">{k}</dt>
              <dd className="min-w-0 break-words text-right text-[11.5px] font-semibold text-[#0F172A]">{v}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  )
}
